import { useCallback, useEffect, useState } from 'react';
import type { Idea, Proyecto } from '../types';
import { storage } from '../lib/storage';
import { encolar } from '../lib/outbox';
import { onDatosCambiados } from '../lib/events';

export function useIdeas() {
  const [proyectos, setProyectos] = useState<Proyecto[]>([]);
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [cargando, setCargando] = useState(true);

  const recargar = useCallback(async () => {
    const [listaProyectos, listaIdeas] = await Promise.all([
      storage.obtenerProyectos(),
      storage.obtenerIdeas(),
    ]);
    setProyectos(listaProyectos);
    setIdeas(listaIdeas);
  }, []);

  useEffect(() => {
    void (async () => {
      await recargar();
      setCargando(false);
    })();
    return onDatosCambiados(() => {
      void recargar();
    });
  }, [recargar]);

  const guardarProyecto = useCallback(
    async (proyecto: Proyecto, esCreate: boolean) => {
      const ahora = new Date().toISOString();
      const conTimestamp: Proyecto = { ...proyecto, updatedAt: ahora };
      await storage.guardarProyecto(conTimestamp);
      await encolar({
        entity: 'proyecto',
        id: proyecto.id,
        type: esCreate ? 'create' : 'update',
        data: { nombre: conTimestamp.nombre },
        clientUpdatedAt: ahora,
      });
      await recargar();
    },
    [recargar]
  );

  // Un solo delete: tanto storage como el servidor borran en cascada las ideas
  // del proyecto.
  const eliminarProyecto = useCallback(
    async (id: string) => {
      await storage.eliminarProyecto(id);
      await encolar({
        entity: 'proyecto',
        id,
        type: 'delete',
        clientUpdatedAt: new Date().toISOString(),
      });
      await recargar();
    },
    [recargar]
  );

  const guardarIdea = useCallback(
    async (idea: Idea, esCreate: boolean) => {
      const ahora = new Date().toISOString();
      const conTimestamp: Idea = { ...idea, updatedAt: ahora };
      await storage.guardarIdea(conTimestamp);
      await encolar({
        entity: 'idea',
        id: idea.id,
        type: esCreate ? 'create' : 'update',
        data: {
          proyectoId: conTimestamp.proyectoId,
          titulo: conTimestamp.titulo,
          notas: conTimestamp.notas,
          hecha: conTimestamp.hecha,
        },
        clientUpdatedAt: ahora,
      });
      await recargar();
    },
    [recargar]
  );

  // Marcar hecha / pendiente conserva la idea con sus anotaciones (sirven para
  // documentar después). Borrar es una acción separada (eliminarIdea).
  const alternarHecha = useCallback(
    async (idea: Idea) => {
      const ahora = new Date().toISOString();
      const actualizada: Idea = { ...idea, hecha: !idea.hecha, updatedAt: ahora };
      await storage.guardarIdea(actualizada);
      await encolar({
        entity: 'idea',
        id: idea.id,
        type: 'update',
        data: { hecha: actualizada.hecha },
        clientUpdatedAt: ahora,
      });
      await recargar();
    },
    [recargar]
  );

  const eliminarIdea = useCallback(
    async (id: string) => {
      await storage.eliminarIdea(id);
      await encolar({
        entity: 'idea',
        id,
        type: 'delete',
        clientUpdatedAt: new Date().toISOString(),
      });
      await recargar();
    },
    [recargar]
  );

  return {
    proyectos,
    ideas,
    cargando,
    guardarProyecto,
    eliminarProyecto,
    guardarIdea,
    alternarHecha,
    eliminarIdea,
  };
}
